import Image from 'next/image';
import Link from 'next/link';
import { Clock, PlayCircle } from 'lucide-react';

interface VideoCardProps {
  id: string;
  title: string;
  channelName: string;
  thumbnail: string | null;
  duration: number | null;
  chunkCount: number;
  hasTranscript: boolean;
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m >= 60
    ? `${Math.floor(m / 60)}:${(m % 60).toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
    : `${m}:${s.toString().padStart(2, '0')}`;
}

export default function VideoCard({ id, title, channelName, thumbnail, duration, chunkCount, hasTranscript }: VideoCardProps) {
  return (
    <Link
      href={`/library/videos/${id}`}
      style={{ border: '1px solid var(--color-border)', backgroundColor: 'var(--color-bg)' }}
      className="group block rounded-xl overflow-hidden transition-all duration-300 hover:border-[var(--color-accent)] hover:shadow-sm"
    >
      <div className="relative aspect-video bg-[var(--color-card)] overflow-hidden">
        {thumbnail ? (
          <Image
            src={thumbnail}
            alt={title}
            fill
            className="object-cover transition-transform duration-500 group-hover:scale-105"
            sizes="(max-width: 768px) 100vw, 33vw"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <PlayCircle size={32} style={{ color: 'var(--color-muted)' }} />
          </div>
        )}
        {duration !== null && (
          <div
            style={{ backgroundColor: 'rgba(28,28,26,0.75)', color: 'white' }}
            className="absolute bottom-2 right-2 text-xs px-2 py-0.5 rounded flex items-center gap-1"
          >
            <Clock size={10} />
            {formatDuration(duration)}
          </div>
        )}
      </div>

      <div className="p-4">
        <p
          style={{ color: 'var(--color-text)' }}
          className="text-sm font-light leading-snug line-clamp-2 mb-2"
        >
          {title}
        </p>
        <div className="flex items-center justify-between mt-2">
          <p style={{ color: 'var(--color-muted)' }} className="text-xs">{channelName}</p>
          <span
            style={{
              backgroundColor: hasTranscript ? 'rgba(138,158,135,0.15)' : 'rgba(180,180,180,0.12)',
              color: hasTranscript ? 'var(--color-accent)' : 'var(--color-muted)',
              border: `1px solid ${hasTranscript ? 'var(--color-accent)' : 'var(--color-border)'}`,
            }}
            className="text-xs px-2 py-0.5 rounded-full flex-shrink-0"
          >
            {hasTranscript ? '書き起こし済み' : '書き起こし未'}
          </span>
        </div>
      </div>
    </Link>
  );
}
